import { JSX, createSignal, createEffect } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { SendOTPResult } from "~/data/auth";
import { createJob } from "~/utils/job";

export interface PhoneModalProps {
  onSubmit: (phoneNumber: string) => Promise<SendOTPResult>;
  onClose: () => void;
}

export function PhoneModal(props: PhoneModalProps): JSX.Element {
  const { components } = useTelescopeContext();

  const [phoneNumber, setPhoneNumber] = createSignal<string>("");
  const [error, setError] = createSignal<string | undefined>(undefined);

  const submitJob = createJob<SendOTPResult>({
    initialJob: async () => {
      if (phoneNumber().length !== 10) {
        throw new Error("Please enter a valid 10-digit phone number");
      }
      return props.onSubmit(phoneNumber());
    },
    errorCallback: (err) => {
      setError(err?.message ?? "Failed to send OTP");
    },
  });

  createEffect(() => {
    if (phoneNumber()) {
      setError(undefined);
    }
  });

  const handleSubmit = (e?: Event) => {
    e?.preventDefault();
    submitJob.run();
  };

  const redirectToWeb = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <div class="flex w-full flex-col ">
      <components.ModalHeader
        size="large"
        title="Log in"
        description="Continue with phone number"
        onClose={props.onClose}
      />

      <form onSubmit={handleSubmit} class="flex flex-col">
        <div class="flex flex-col gap-4 p-5">
          <components.Input
            value={phoneNumber()}
            onChange={(value: string) =>
              setPhoneNumber(value.replace(/\D/g, ""))
            }
            placeholder="Enter mobile no."
            size={40}
            type="phone"
            label="Phone no."
            required={true}
            state={error() ? "error" : "default"}
            helperText={error()}
          />
        </div>

        <components.ModalFooter
          button={{
            text: "Continue",
            onClick: handleSubmit,
            variant: "primary",
            style: "neutral",
            size: 40,
            loading: submitJob.isRunning(),
            disabled: submitJob.isRunning(),
          }}
        />

        <div class="pb-4">
          <p class="text-center text-label-regular text-text-normal-secondary">
            By continuing you agree to our{" "}
            <span
              class="cursor-pointer underline"
              onClick={() =>
                redirectToWeb("https://www.myhubble.money/privacy-policy")
              }
            >
              Privacy policy
            </span>{" "}
            and{" "}
            <span
              class="cursor-pointer underline"
              onClick={() =>
                redirectToWeb("https://www.myhubble.money/terms-and-conditions")
              }
            >
              Terms & conditions
            </span>
          </p>
        </div>
      </form>
    </div>
  );
}
